// Shared UI Components for Recon Module
import React, { useState, useRef } from 'react';
import api from '../../services/api';
import { RO_STATUSES } from './constants';

// Status Badge Component
export const StatusBadge = ({ status, size = 'sm' }) => {
  const config = RO_STATUSES.find(s => s.value === status) || { label: status || 'Unknown', color: 'gray' };
  const colors = {
    blue: 'bg-blue-100 text-blue-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    orange: 'bg-orange-100 text-orange-800',
    pink: 'bg-pink-100 text-pink-800',
    purple: 'bg-purple-100 text-purple-800',
    green: 'bg-green-100 text-green-800',
    gray: 'bg-neutral-100 text-neutral-600',
    red: 'bg-red-100 text-red-800',
  };
  const sizeClass = size === 'lg' ? 'px-3 py-1 text-sm' : 'px-2 py-0.5 text-xs';
  return (
    <span className={`${sizeClass} rounded font-medium ${colors[config.color] || colors.gray}`}>
      {config.label}
    </span>
  );
};

// File Upload with Drag & Drop
export const FileUpload = ({ accept, multiple, onUpload, children, className, disabled }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setIsDragging(true);
    } else if (e.type === 'dragleave') {
      setIsDragging(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (disabled) return;
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await uploadFiles(files);
    }
  };

  const handleChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      await uploadFiles(files);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const uploadFiles = async (files) => {
    setUploading(true);
    for (const file of files) {
      try {
        // Determine upload type based on file mimetype and extension
        const fileName = file.name.toLowerCase();
        const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
        const isPDF = file.type === 'application/pdf' || fileExtension === '.pdf';
        const isDocument = file.type.includes('document') || 
                         fileExtension === '.doc' || 
                         fileExtension === '.docx' || 
                         fileExtension === '.xls' || 
                         fileExtension === '.xlsx';
        const isVideo = file.type.startsWith('video/') || 
                       ['.mp4', '.mov', '.avi', '.webm', '.qt'].includes(fileExtension);
        
        let response;
        if (isVideo) {
          response = await api.uploadVideo(file);
        } else if (isPDF || isDocument) {
          response = await api.uploadDocument(file);
        } else {
          response = await api.uploadImage(file);
        }
        
        if (response && response.url) {
          onUpload(response.url);
        }
      } catch (error) {
        console.error('Upload failed:', error);
        alert(`Failed to upload ${file.name}: ${error.message}`);
      }
    }
    setUploading(false);
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (!disabled && inputRef.current) {
      inputRef.current.click();
    }
  };

  return (
    <div
      className={`${className} ${isDragging ? 'border-blue-500 bg-blue-50' : ''} ${uploading ? 'opacity-50' : ''} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={handleClick}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={handleChange}
        disabled={disabled}
      />
      {uploading ? (
        <div className="text-center">
          <div className="text-2xl mb-1"></div>
          <div className="text-sm text-neutral-500">Uploading...</div>
        </div>
      ) : (
        children
      )}
    </div>
  );
};

// Section Header Component
export const SectionHeader = ({ letter, title, action }) => (
  <div className="flex justify-between items-center mb-3">
    <h2 className="font-semibold flex items-center gap-2">
      <span className="w-6 h-6 bg-blue-600 text-white rounded text-xs flex items-center justify-center font-bold">
        {letter}
      </span>
      {title}
    </h2>
    {action}
  </div>
);

// Loading Spinner
export const LoadingSpinner = ({ message = 'Loading...' }) => (
  <div className="flex items-center justify-center h-64 text-neutral-400">
    <div className="text-center">
      <div className="text-3xl mb-2"></div>
      <div>{message}</div>
    </div>
  </div>
);

// Error Display
export const ErrorDisplay = ({ message = 'Something went wrong' }) => (
  <div className="flex items-center justify-center h-64 text-red-500">
    <div className="text-center">
      <div className="text-3xl mb-2"></div>
      <div>{message}</div>
    </div>
  </div>
);

// Confirmation Modal
export const ConfirmModal = ({ title, message, onConfirm, onCancel, confirmText = 'Confirm', cancelText = 'Cancel', danger = false }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onCancel}>
    <div className="bg-white w-full max-w-md rounded-xl shadow-xl p-6" onClick={e => e.stopPropagation()}>
      <h3 className="font-semibold text-lg mb-2">{title}</h3>
      <p className="text-neutral-600 mb-6">{message}</p>
      <div className="flex justify-end gap-3">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-neutral-600 hover:bg-neutral-100 rounded-lg"
        >
          {cancelText}
        </button>
        <button
          onClick={onConfirm}
          className={`px-4 py-2 rounded-lg text-white ${danger ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
        >
          {confirmText}
        </button>
      </div>
    </div>
  </div>
);

// Photo Gallery with Delete
export const PhotoGallery = ({ photos, onDelete, editable = true }) => {
  if (!photos || photos.length === 0) return null;
  
  // Handle both string URLs and object with url property
  const getPhotoUrl = (photo) => {
    if (typeof photo === 'string') return photo;
    if (photo?.url) return photo.url;
    return null;
  };
  
  return (
    <div className="flex gap-2 flex-wrap">
      {photos.map((photo, index) => {
        const url = getPhotoUrl(photo);
        if (!url) return null;
        
        return (
        <div key={index} className="relative group">
          <a href={url} target="_blank" rel="noopener noreferrer">
            <img
              src={url}
              alt={`Photo ${index + 1}`}
              className="w-20 h-20 object-cover rounded border hover:opacity-80 transition"
              onError={(e) => {
                console.error('[PhotoGallery] Failed to load image:', url);
                e.target.style.display = 'none';
              }}
            />
          </a>
          {editable && onDelete && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDelete(index);
              }}
              className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition hover:bg-red-600"
            >
              ×
            </button>
          )}
        </div>
        );
      })}
    </div>
  );
};

// Date Input with proper formatting
export const DateInput = ({ value, onChange, label, className = '' }) => {
  // Convert ISO date to YYYY-MM-DD format for input
  const formatForInput = (dateValue) => {
    if (!dateValue) return '';
    if (typeof dateValue === 'string') {
      // Handle ISO strings
      return dateValue.split('T')[0];
    }
    return '';
  };

  return (
    <div className={className}>
      {label && <label className="block text-xs text-neutral-500 mb-1">{label}</label>}
      <input
        type="date"
        value={formatForInput(value)}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-9 px-3 border border-neutral-200 rounded-lg bg-white text-sm"
      />
    </div>
  );
};
